<template>
  <div class="p-results-check-ranks-metrics">
    <div class="p-results-check-ranks-metrics__header">
      <h3 class="c-title c-title--h03">
        <Icon variant="sitesummary" :size="16" radius />
        <span class="title">「{{ selected.keyword }}」の成果推移</span>
      </h3>
      <Calendar
        :range="metricsPeriod.range"
        :value="metricsPeriod.value"
        listType="gsc"
        @input="updatePeriod"
      />
    </div>
    <Error v-if="!selected.id" variant="inline">
      <template slot="title">
        キーワードが選択されていません。
      </template>
      <p>
        詳細な成果を確認したいキーワードを上部のリストから選択してください。
      </p>
    </Error>
    <template v-else>
      <ListMetrics
        :checked="checked"
        gaType="ga4"
        :items="summary.data.items"
        :itemData="itemData"
        :period="metricsPeriod.value"
        :isLoading="summary.isLoading"
        @change="updateChecked"
      />
      <div class="p-seed-keywords-graph">
        <GraphKeyword
          :checked="checked"
          :data="term.data"
          :itemData="itemData"
          :period="metricsPeriod.value"
          :project="project"
          :tasks="task.data.items"
          :keywordId="selected.id"
          :isLoading="term.isLoading"
          :selected="selected"
        />
        <slot></slot>
      </div>
    </template>
  </div>
</template>

<script>
import { CALENDAR } from '@/constants';
import Calendar from '@/components/Calendar';
import Error from '@/components/Error';
import GraphKeyword from '@/components/siteImportantKeywordGA4/GraphKeyword';
import Icon from '@/components/Icon';
import ListMetrics from '@/components/ListMetrics';
import apiProjects from '@/api/projects';
import {
  getProjectObservationKeywordMetricsSummary,
  getProjectObservationKeywordMetricsTerm
} from '@/api/v2/projects';
import utils from '@/utils';
import axios from 'axios';

export default {
  props: {
    device: {
      type: String,
      required: true
    },
    listPeriod: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    selected: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    }
  },
  components: {
    Calendar,
    Error,
    GraphKeyword,
    Icon,
    ListMetrics
  },
  data() {
    return {
      cancelToken: {
        summary: null,
        tasks: null,
        term: null
      },
      checked: this.$store.state.page.siteImportantKeyword.checked,
      itemData: {
        position: {
          color: 'purple',
          icon: 'rank_change',
          label: '平均順位',
          type: 'rank',
          unit: '位'
        },
        clicks: {
          color: 'green',
          icon: 'mouse',
          label: 'クリック数',
          type: 'number',
          unit: '回'
        },
        ctr: {
          color: 'pink',
          icon: 'percent',
          label: 'クリック率',
          type: 'percentage',
          unit: '%'
        },
        conversion: {
          color: 'light-blue',
          icon: 'conversion',
          label: '貢献CV',
          type: 'number'
        }
      },
      summary: {
        data: {
          items: {}
        },
        isLoading: true
      },
      task: {
        data: {
          items: []
        }
      },
      term: {
        data: {
          items: []
        },
        isLoading: true
      },
      metricsPeriod: {
        range: [...this.listPeriod.range],
        value: [...this.listPeriod.value]
      }
    };
  },
  watch: {
    device: {
      handler() {
        this.getData();
      }
    },
    'selected.id': {
      handler() {
        this.getData();
        this.getTasks();
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.cancelRequests();
  },
  methods: {
    cancelRequests() {
      if (this.cancelToken.summary) {
        this.cancelToken.summary.cancel();
      }
      if (this.cancelToken.tasks) {
        this.cancelToken.tasks.cancel();
      }
      if (this.cancelToken.term) {
        this.cancelToken.term.cancel();
      }
    },
    getData() {
      this.getSummary();
      this.getTerm();
    },
    getSummary() {
      if (!this.selected.id) {
        return;
      }
      if (this.cancelToken.summary) {
        this.cancelToken.summary.cancel();
      }
      this.summary.isLoading = true;
      this.summary.data.items = {};

      const params = {
        country: 'JPN',
        device: this.device,
        from: utils.formatDate(this.metricsPeriod.value[0]),
        to: utils.formatDate(this.metricsPeriod.value[1])
      };
      this.cancelToken.summary = axios.CancelToken.source();

      getProjectObservationKeywordMetricsSummary({
        cancelToken: this.cancelToken.summary.token,
        idToken: this.auth.idToken,
        keywordId: this.selected.id,
        params,
        projectId: this.project.id
      })
        .then(response => {
          this.summary.data.items = response.data;
          this.summary.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.summary.isLoading = false;
          }
        });
    },
    getTasks() {
      if (!this.selected.id) {
        return;
      }
      if (this.cancelToken.tasks) {
        this.cancelToken.tasks.cancel();
      }
      this.task.data.items = [];

      const params = {
        columns: 'done',
        observationKeywordId: this.selected.id,
        order: 'createdDate:desc'
      };
      this.cancelToken.tasks = axios.CancelToken.source();

      return apiProjects.tasks
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          this.cancelToken.tasks.token
        )
        .then(response => {
          this.task.data.items = response.data.items;
        })
        .catch(error => {});
    },

    getTerm() {
      if (!this.selected.id) {
        return;
      }
      if (this.cancelToken.term) {
        this.cancelToken.term.cancel();
      }
      this.term.isLoading = true;
      this.term.data.items = [];

      const params = {
        country: 'JPN',
        device: this.device,
        from: utils.formatDate(this.metricsPeriod.value[0]),
        to: utils.formatDate(this.metricsPeriod.value[1]),
        type: 'term'
      };
      this.cancelToken.term = axios.CancelToken.source();

      getProjectObservationKeywordMetricsTerm({
        cancelToken: this.cancelToken.term.token,
        idToken: this.auth.idToken,
        keywordId: this.selected.id,
        params,
        projectId: this.project.id
      })
        .then(response => {
          let items = [];
          const total = response.data.items.length;
          for (let i = 0; i < total; i++) {
            const item = response.data.items[i];
            const date = utils.getDateResetHours(item.date);
            // 指定期間範囲内のデータのみitemsに入れる
            // FakeAPIの場合、期間を1日のみ選択した時でも全データを返してきて、グラフの表示がおかしくなるため
            // FakeじゃないAPIの場合でも、直近3日間のデータはカットする
            if (
              date >= this.metricsPeriod.value[0] &&
              date <= this.metricsPeriod.value[1] &&
              date <= CALENDAR.gsc.maxDate
            ) {
              item.date = date;
              if (item.ctr) {
                item.ctr *= 100;
              }
              items.push(item);
            }
          }
          this.term.data.items = items;
          this.term.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.term.isLoading = false;
          }
        });
    },
    updateChecked(checked) {
      this.checked = checked;

      this.$store.dispatch('page/setStatus', {
        page: 'siteImportantKeyword',
        status: {
          checked: checked
        }
      });
    },
    updatePeriod(value) {
      this.metricsPeriod.value = value;
      this.getData();
    }
  }
};
</script>
